import React, { useEffect, useState } from "react";
import {
  Calendar,
  Clock,
  Plus,
  ChevronLeft,
  ChevronRight,
  X,
  Video,
  Home,
  Settings,
  List,
  PlusCircle,
  LogOut,
  ClipboardList,
} from "lucide-react";
import axios from "axios";
import Loader from "./loader/loader.js";
import DashboardLoader from "./loader/DashboardLoader.js";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";

const VideoPhasesCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [planVideos, setPlanVideos] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [activeSidebarItem, setActiveSidebarItem] = useState("calendar");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay };
  };

  const handleSidebarItemClick = (itemId, path) => {
    navigate(path);

    setActiveSidebarItem(itemId);
  };

  const { days, firstDay } = getDaysInMonth(currentDate);

  const handlePrevMonth = async () => {
    const newDate = new Date(currentDate.setMonth(currentDate.getMonth() - 1));
    setCurrentDate(newDate);
    await fetchPlanVideos();
  };

  const handleNextMonth = async () => {
    const newDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
    setCurrentDate(newDate);
    await fetchPlanVideos();
  };

  const handleDateClick = (day, activePhases) => {
    const dateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setSelectedDate(dateString);
    setSelectedDateEvents(activePhases || []);
    setIsDrawerOpen(true);
  };

  const isDateInPhase = (date, phase) => {
    const currentDate = new Date(date);
    const startDate = new Date(phase.startDate);
    const endDate = new Date(phase.endDate);
    return currentDate >= startDate && currentDate <= endDate;
  };

  const fetchPlanVideos = async (date = currentDate) => {
    setLoading(true);
    try {
      const year = date.getFullYear();
      const month = date
        .toLocaleString("default", { month: "long" })
        .toUpperCase();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-plans-for-month`,
        {
          params: {
            month,
            year,
          },
          withCredentials: true,
        },
      );
      const data = await response.data.videos;
      setPlanVideos(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching plan videos:", error);
      setLoading(false);
    }
  };

  const PhaseCard = ({ phase, isSelected, idx }) => (
    <div
      className="text-xs p-2 rounded hover:opacity-90 transition-opacity mb-2"
      style={{ backgroundColor: phase.color }}
    >
      <div className="font-medium truncate">{phase.topic}</div>
      <div className="flex items-center mt-1">
        <Video size={12} className="mr-1 flex-shrink-0" />
        <span className="truncate">{phase.name}</span>
      </div>
    </div>
  );

  const DateCell = ({ day, dateString, activePhases }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const isToday = new Date().toISOString().split("T")[0] === dateString;
    const hasOverflow = activePhases?.length > 5;

    return (
      <div
        onClick={() => handleDateClick(day, activePhases)}
        onMouseEnter={() => hasOverflow && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`relative bg-white p-2 min-h-[8rem] hover:bg-gray-50 cursor-pointer ${
          selectedDate === dateString ? "ring-2 ring-blue-500" : ""
        }`}
      >
        <div
          className={`font-medium text-sm mb-1 ${isToday ? "text-blue-600" : ""}`}
        >
          {day}
          {isToday && (
            <span className="ml-1 text-xs text-blue-600">(Today)</span>
          )}
        </div>

        <div className="space-y-1 overflow-y-auto max-h-24 custom-scrollbar">
          {activePhases?.slice(0, 5)?.map((phase, idx) => (
            <PhaseCard
              key={`${phase.topic}-${phase.name}-${idx}`}
              phase={phase}
              idx={idx}
            />
          ))}
          {hasOverflow && (
            <div className="text-xs text-gray-500 font-medium">
              +{activePhases.length - 3} more
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchPlanVideos();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        activeSidebarItem={activeSidebarItem}
        onSidebarItemClick={handleSidebarItemClick}
      />

      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <DashboardLoader />
        </div>
      ) : (
        <div className="flex-1 flex">
          {/* Calendar Section */}
          <div
            className={`flex-1 transition-all duration-300 ${isDrawerOpen ? "mr-96" : ""}`}
          >
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">
                  Video Production Calendar
                </h1>
              </div>

              {/* Calendar Grid */}
              <div className="bg-white rounded-lg shadow">
                {/* Calendar Header */}
                <div className="p-4 border-b flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handlePrevMonth}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <h2 className="text-xl font-semibold">
                      {currentDate.toLocaleString("default", {
                        month: "long",
                        year: "numeric",
                      })}
                    </h2>
                    <button
                      onClick={handleNextMonth}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-px bg-gray-200">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]?.map(
                    (day) => (
                      <div
                        key={day}
                        className="bg-gray-50 p-2 text-center text-sm font-medium text-gray-700"
                      >
                        {day}
                      </div>
                    ),
                  )}
                </div>

                {/* Calendar Dates */}
                <div className="grid grid-cols-7 gap-px bg-gray-200">
                  {[...Array(firstDay)]?.map((_, index) => (
                    <div
                      key={`empty-${index}`}
                      className="bg-white p-2 min-h-[8rem]"
                    />
                  ))}

                  {[...Array(days)]?.map((_, index) => {
                    const day = index + 1;
                    const dateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

                    const activePhases = planVideos?.flatMap((video) =>
                      video.phases
                        .filter((phase) => isDateInPhase(dateString, phase))
                        ?.map((phase) => ({
                          ...phase,
                          topic: video.topic,
                          color: video.color,
                        })),
                    );

                    return (
                      <DateCell
                        key={day}
                        day={day}
                        dateString={dateString}
                        activePhases={activePhases}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Side Drawer */}
          {isDrawerOpen && (
            <div
              className={`
          w-96 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
          fixed right-0 top-0 h-full
          ${isDrawerOpen ? "translate-x-0" : "translate-x-full"}
        `}
            >
              {/* Drawer Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedDate &&
                      new Date(selectedDate).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                  </h3>
                  <button
                    onClick={() => setIsDrawerOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Drawer Content */}
              <div className="p-4 overflow-y-auto max-h-[calc(100vh-5rem)]">
                {selectedDateEvents.length === 0 ? (
                  <p className="text-gray-500">
                    No events scheduled for this date
                  </p>
                ) : (
                  selectedDateEvents.map((phase, idx) => (
                    <PhaseCard
                      key={`drawer-${phase.topic}-${phase.name}-${idx}`}
                      phase={phase}
                      idx={idx}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPhasesCalendar;
